import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHouse, faCheckToSlot, faUserTie, faSitemap, faTriangleExclamation,
  faBuildingColumns, faNewspaper, faClipboardCheck, faUsers, faLocationDot, faUserSlash, faHandshake,
  faLightbulb, faChartPie, faChartColumn, faMagnifyingGlass, faPrint, faRotate, faCalendarDays,
  faCircleExclamation, faUserCheck, faCity, faFlag, faLock, faPersonBooth,
} from "@fortawesome/free-solid-svg-icons";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

/**
 * Assembly Constituency Proformas ("AC Bible") - Project Andhra Pradesh.
 *
 * A constituency assessment dashboard built from the parsed proforma sheet
 * (/api/proformas). Every figure on the page comes from the sheet; panels with
 * no data say so rather than showing placeholders. Contact numbers arrive
 * masked; revealing one is an explicit, logged action.
 */

const API_URL = process.env.REACT_APP_API_URL;

/* --------------------------------- theme ---------------------------------- */
const NAVY = "#0b2350";
const SIDEBAR = "#112D4E";
const NAVY2 = "#16407f";
const INK = "#1b2a3d";
const MUTED = "#64748b";
const LINE = "#e3e9f2";
const BG = "#eef2f8";
const BLUE = "#1d4ed8";
const LINK = "#2563eb";
const GREEN = "#2e7d32";
const AMBER = "#d97706";
const RED = "#c62828";
const PURPLE = "#7e3fb0";
const TEAL = "#00897b";

const GRAD = {
  navy: "linear-gradient(135deg,#0d2a5e 0%,#1b4588 100%)",
  green: "linear-gradient(135deg,#2e7d32 0%,#4caf50 100%)",
  purple: "linear-gradient(135deg,#6a1b9a 0%,#9c4dcc 100%)",
  orange: "linear-gradient(135deg,#e8710a 0%,#f6a623 100%)",
  teal: "linear-gradient(135deg,#00796b 0%,#26a69a 100%)",
  red: "linear-gradient(135deg,#b71c1c 0%,#e53935 100%)",
  blue: "linear-gradient(135deg,#1565c0 0%,#42a5f5 100%)",
};

const PARTY = {
  TDP: "#e0a800", YSRCP: "#1a5fb4", INC: "#0f9bd7", JSP: "#c62828",
  BJP: "#f57c00", PRP: "#7b1fa2", IND: "#78909c",
};
const partyKey = (p) => {
  const k = String(p || "").toUpperCase();
  return Object.keys(PARTY).find((n) => k.includes(n)) || "";
};
const partyColor = (p) => PARTY[partyKey(p)] || "#94a3b8";

const toNum = (v) => {
  const n = Number(String(v == null ? "" : v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};
const fmt = (v) => {
  const n = toNum(v);
  return n == null || String(v).trim() === "" ? String(v == null ? "" : v) : n.toLocaleString("en-IN");
};
const has = (obj, q) => !q || JSON.stringify(obj).toLowerCase().includes(q);
const titleCase = (s) => String(s || "").replace(/\b([a-z])([a-z]*)/g, (m, a, b) => a.toUpperCase() + b);

const STATUS_COLOR = (s) => {
  const t = String(s || "").toLowerCase();
  if (/not\s*resolved|inactive|difficult|weak/.test(t)) return RED;
  if (/partial|battle|pending|progress/.test(t)) return AMBER;
  if (/active|resolved|strong|favou?rable|done|completed/.test(t)) return GREEN;
  return MUTED;
};

/* Table header: blue background, bold white, centred both ways. */
const TH = {
  background: BLUE, color: "#fff", fontWeight: 800, fontSize: 12, letterSpacing: ".3px",
  textAlign: "center", verticalAlign: "middle", padding: "10px 10px",
  border: "1px solid #1e40af", whiteSpace: "nowrap",
};
const TD = { padding: "9px 10px", borderBottom: `1px solid ${LINE}`, textAlign: "center", verticalAlign: "middle", color: "#334155" };

const NAV = [
  ["overview", "Dashboard", faHouse],
  ["profile", "Constituency Profile", faCity],
  ["elections", "Electoral History", faCheckToSlot],
  ["caste", "Caste & Community", faUsers],
  ["leaders", "Leadership", faUserTie],
  ["organisation", "Organisation & Booths", faSitemap],
  ["risk", "Risks & Opportunities", faTriangleExclamation],
  ["governance", "Governance & Issues", faBuildingColumns],
  ["media", "Media", faNewspaper],
  ["other", "Other Sheets", faClipboardCheck],
  ["quality", "Data Completeness", faClipboardCheck],
];

/* ------------------------------ small pieces ------------------------------ */

const label = { fontSize: 10.5, fontWeight: 800, color: MUTED, letterSpacing: ".6px", textTransform: "uppercase" };

const Pill = ({ children, color = MUTED, solid = false }) => (
  <span style={{
    display: "inline-block", borderRadius: 99, padding: "2px 9px", fontSize: 11, fontWeight: 700,
    background: solid ? color : `${color}1a`, color: solid ? "#fff" : color, whiteSpace: "nowrap",
  }}>{children}</span>
);

const PartyChip = ({ party }) => (party ? <Pill color={partyColor(party)} solid>{String(party).toUpperCase()}</Pill> : null);

const Clamp = ({ text, lines = 3 }) => {
  const [open, setOpen] = useState(false);
  const t = String(text || "").trim();
  if (!t) return null;
  const long = t.length > lines * 90 || t.split("\n").length > lines;
  return (
    <div>
      <div style={{
        whiteSpace: "pre-wrap", fontSize: 13, color: "#334155", lineHeight: 1.6,
        ...(open || !long ? {} : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" }),
      }}>{t}</div>
      {long && (
        <button type="button" className="no-print" onClick={() => setOpen((v) => !v)}
          style={{ background: "none", border: "none", color: LINK, fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "2px 0" }}>
          {open ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
};

const Empty = ({ children }) => (
  <div style={{ border: `1px dashed #cfd8e6`, borderRadius: 10, padding: "18px 16px", color: MUTED, fontSize: 13, textAlign: "center", background: "#f8fafd" }}>
    {children}
  </div>
);

/** White card with a navy header bar - the building block of the page. */
const Panel = ({ id, icon, title, right, children, pad = 16 }) => (
  <section id={id ? `sec-${id}` : undefined} style={{
    background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 10px rgba(15,35,80,.08)",
    border: `1px solid ${LINE}`, scrollMarginTop: 14, breakInside: "avoid", display: "flex", flexDirection: "column",
  }}>
    <div style={{
      background: `linear-gradient(90deg, ${NAVY} 0%, ${NAVY2} 100%)`, color: "#fff",
      padding: "11px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 800, fontSize: 15.5 }}>
        {icon && <FontAwesomeIcon icon={icon} />}
        <span>{title}</span>
      </div>
      {right && <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.95 }}>{right}</div>}
    </div>
    <div style={{ padding: pad, flex: 1 }}>{children}</div>
  </section>
);

const SubHead = ({ children, count }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "16px 0 10px" }}>
    <div style={{ width: 4, height: 16, background: BLUE, borderRadius: 2 }} />
    <div style={{ fontSize: 14.5, fontWeight: 800, color: INK }}>{children}</div>
    {count != null && <Pill color={BLUE}>{count}</Pill>}
  </div>
);

/* --------------------------------- charts --------------------------------- */

// perceived brightness of a #rrggbb colour (0 dark .. 1 light)
const lightness = (hex) => {
  const m = String(hex).replace("#", "").match(/^([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return 0;
  const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

const Donut = ({ segments, size = 140, thickness = 34, centerValue, centerLabel, labelOf }) => {
  const tot = segments.reduce((sum, y) => sum + (y.value || 0), 0) || 1;
  const anySmall = segments.some((y) => y.value > 0 && y.value / tot < 0.08);
  const pad = anySmall ? 18 : 2; // room for labels on small slices, outside the ring
  const W = size + pad * 2;
  const cx = W / 2;
  const total = segments.reduce((sum, y) => sum + (y.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const segs = segments.filter((y) => y.value > 0);
  let acc = 0;
  const placed = segs.map((sg) => {
    const frac = total ? sg.value / total : 0;
    const start = acc;
    acc += frac;
    const ang = (start + frac / 2) * 2 * Math.PI - Math.PI / 2;
    const inside = frac >= 0.08;
    const rr = inside ? r : r + thickness / 2 + 13;
    return { ...sg, frac, start, x: cx + rr * Math.cos(ang), y: cx + rr * Math.sin(ang), inside };
  });
  return (
    <svg width={W} height={W} viewBox={`0 0 ${W} ${W}`} style={{ flexShrink: 0, maxWidth: "100%" }}>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke="#edf1f7" strokeWidth={thickness} />
      {placed.map((sg) => (
        <circle key={sg.label} cx={cx} cy={cx} r={r} fill="none" stroke={sg.color} strokeWidth={thickness}
          strokeDasharray={`${sg.frac * c} ${c - sg.frac * c}`} strokeDashoffset={-sg.start * c}
          transform={`rotate(-90 ${cx} ${cx})`} />
      ))}
      {placed.map((sg) => (
        <text key={`t-${sg.label}`} x={sg.x} y={sg.y} textAnchor="middle" dominantBaseline="central"
          style={sg.inside
            ? (lightness(sg.color) > 0.72
              ? { fontSize: 10.5, fontWeight: 800, fill: INK }
              : { fontSize: 10.5, fontWeight: 800, fill: "#fff", stroke: sg.color, strokeWidth: 3, paintOrder: "stroke", strokeLinejoin: "round" })
            : { fontSize: 10.5, fontWeight: 800, fill: INK }}>
          {labelOf ? labelOf(sg) : `${Math.round(sg.frac * 1000) / 10}%`}
        </text>
      ))}
      <text x="50%" y="45%" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 19, fontWeight: 800, fill: INK }}>{centerValue}</text>
      <text x="50%" y="59%" textAnchor="middle" dominantBaseline="central" style={{ fontSize: 10, fontWeight: 600, fill: MUTED }}>{centerLabel}</text>
    </svg>
  );
};

const Legend = ({ segments }) => {
  const total = segments.reduce((sum, y) => sum + (y.value || 0), 0) || 1;
  return (
    <div style={{ display: "grid", gap: 6, flex: "1 1 0", minWidth: 0 }}>
      {segments.map((sg) => (
        <div key={sg.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
          border: `1px solid ${LINE}`, borderRadius: 8, padding: "7px 10px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: INK, fontWeight: 600 }}>
            <span style={{ width: 11, height: 11, borderRadius: "50%", background: sg.color, flexShrink: 0 }} />{sg.label}
          </span>
          <span style={{ whiteSpace: "nowrap" }}>
            <strong style={{ fontSize: 14, color: INK }}>{fmt(sg.value)}</strong>
            <span style={{ fontSize: 12, color: MUTED }}> · {Math.round((sg.value / total) * 1000) / 10}%</span>
          </span>
        </div>
      ))}
    </div>
  );
};

const Bars = ({ data, plotH = 150 }) => {
  const top = Math.max(...data.map((y) => y.value), 1);
  const step = Math.max(1, Math.ceil((top * 1.15) / 4)); // headroom so the tallest value label always fits
  const max = step * 4;
  const ticks = [0, 1, 2, 3, 4].map((i) => i * step);
  const LABEL_H = 20;
  const barArea = plotH - LABEL_H;
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <div style={{ position: "relative", width: 26, height: plotH, flexShrink: 0 }}>
        {ticks.map((t) => (
          <div key={t} style={{ position: "absolute", right: 0, bottom: (t / max) * barArea - 7, fontSize: 11, color: MUTED }}>{t}</div>
        ))}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ position: "relative", height: plotH, borderLeft: `1px solid ${LINE}`, borderBottom: `1px solid #cfd8e6` }}>
          {ticks.slice(1).map((t) => (
            <div key={t} style={{ position: "absolute", left: 0, right: 0, bottom: (t / max) * barArea, borderTop: "1px dashed #e3e9f2" }} />
          ))}
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "flex-end", gap: "2.5%", padding: "0 2%" }}>
            {data.map((y) => (
              <div key={y.label} style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%" }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: INK, height: LABEL_H, lineHeight: `${LABEL_H}px` }}>{y.value}</div>
                <div title={`${y.full || y.label}: ${y.value}`} style={{ width: "100%", maxWidth: 40, height: Math.max((y.value / max) * barArea, 3),
                  background: y.color, borderRadius: "5px 5px 0 0" }} />
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: "2.5%", padding: "5px 2% 0" }}>
          {data.map((y) => (
            <div key={y.label} title={y.full || y.label} style={{ flex: "1 1 0", minWidth: 0, textAlign: "center", fontSize: 11,
              fontWeight: 600, color: MUTED, lineHeight: 1.2, wordBreak: "break-all" }}>{y.label}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* --------------------------------- phone ---------------------------------- */

const Phone = ({ ph, revealed, onReveal }) => {
  if (!ph) return null;
  if (typeof ph === "string") return <span>{ph}</span>;
  const full = revealed[ph.ref];
  if (full) return <a href={`tel:${full}`} style={{ color: LINK, fontWeight: 700, textDecoration: "none" }}>{full}</a>;
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      <span style={{ fontFamily: "monospace", color: "#475569" }}>{ph.masked}</span>{" "}
      <button type="button" className="no-print" onClick={() => onReveal(ph.ref)} title="Reveal this number (logged)"
        style={{ background: "#e8efff", color: LINK, border: "none", borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
        Show
      </button>
    </span>
  );
};

/* ------------------------------ people cards ------------------------------ */

const PERSON_TEXT = [
  ["reason", "Why"], ["actionable", "Suggested actionable"], ["assessment", "Assessment"],
  ["profile", "Profile"], ["activity", "Recent activity"], ["remarks", "Remarks"],
];

const PersonCard = ({ p, revealed, onReveal, accent }) => {
  const f = p.fields;
  const color = accent || partyColor(f.party);
  const meta = [f.caste && titleCase(f.caste), f.casteCategory, f.age && `${f.age} yrs`, f.gender, f.occupation && titleCase(f.occupation)]
    .filter(Boolean).join(" · ");
  const where = [f.village, f.mandal].filter(Boolean).map(titleCase).join(", ");
  return (
    <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, overflow: "hidden", breakInside: "avoid",
                  display: "flex", flexDirection: "column", boxShadow: "0 1px 4px rgba(15,35,80,.05)" }}>
      <div style={{ height: 4, background: color }} />
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15.5, fontWeight: 800, color: INK, lineHeight: 1.25 }}>{titleCase(f.name) || "—"}</div>
            {f.designation && <div style={{ fontSize: 12.5, color: "#475569", marginTop: 2 }}>{titleCase(f.designation)}</div>}
          </div>
          <PartyChip party={f.party} />
        </div>
        {meta && <div style={{ fontSize: 12, color: MUTED }}>{meta}</div>}
        {where && <div style={{ fontSize: 12, color: MUTED }}>{where}</div>}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {f.status && <Pill color={STATUS_COLOR(f.status)}>{f.status}</Pill>}
          {f.influence && <Pill color={NAVY}>Influence: {f.influence}</Pill>}
          {f.votes && <Pill color={BLUE}>{f.votes} votes</Pill>}
        </div>
        {f.phone && (
          <div style={{ fontSize: 12.5 }}>
            <span style={{ color: MUTED }}>Phone: </span>
            <Phone ph={f.phone} revealed={revealed} onReveal={onReveal} />
          </div>
        )}
        {PERSON_TEXT.filter(([key]) => f[key]).map(([key, l]) => (
          <div key={key}>
            <div style={label}>{l}</div>
            <Clamp text={f[key]} lines={key === "reason" || key === "actionable" ? 4 : 3} />
          </div>
        ))}
        {p.extra.map((y) => (
          <div key={y.label}>
            <div style={label}>{y.label}</div>
            <Clamp text={y.value} lines={2} />
          </div>
        ))}
      </div>
    </div>
  );
};

const PeopleGrid = ({ people, q, revealed, onReveal, accent, emptyText }) => {
  const list = people.filter((p) => has(p.fields, q) || has(p.extra, q));
  if (!people.length) return <Empty>{emptyText || "No entries recorded in the sheet yet."}</Empty>;
  if (!list.length) return <Empty>No matches for this search.</Empty>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 12 }}>
      {list.map((p) => <PersonCard key={p.id} p={p} revealed={revealed} onReveal={onReveal} accent={accent} />)}
    </div>
  );
};

/* --------------------------------- tables --------------------------------- */

const TableShell = ({ children }) => (
  <div style={{ overflowX: "auto", border: `1px solid ${LINE}`, borderRadius: 10, background: "#fff" }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.8 }}>{children}</table>
  </div>
);

const DataTable = ({ columns, rows, q, revealed, onReveal, emptyText }) => {
  const cols = (columns || []).filter((c) => c.canon !== "sno");
  const list = (rows || []).filter((r) => has(r, q));
  if (!rows || !rows.length) return <Empty>{emptyText || "No entries recorded in the sheet yet."}</Empty>;
  if (!list.length) return <Empty>No matches for this search.</Empty>;
  return (
    <TableShell>
      <thead><tr>{cols.map((c) => <th key={c.key} style={TH}>{c.label}</th>)}</tr></thead>
      <tbody>
        {list.map((r, i) => (
          <tr key={i} style={{ background: i % 2 ? "#f8fafd" : "#fff" }}>
            {cols.map((c) => {
              const v = r[c.key];
              const long = typeof v === "string" && v.length > 90;
              return (
                <td key={c.key} style={{ ...TD, minWidth: long ? 260 : undefined }}>
                  {v && typeof v === "object" ? <Phone ph={v} revealed={revealed} onReveal={onReveal} />
                    : c.canon === "party" || /^party$|inclination/i.test(c.label) ? (v ? <PartyChip party={v} /> : "—")
                      : c.canon === "status" ? (v ? <Pill color={STATUS_COLOR(v)}>{v}</Pill> : "—")
                        : long ? <Clamp text={v} lines={3} /> : (v || "—")}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
};

const PeopleTable = ({ people, q, revealed, onReveal }) => {
  const list = people.filter((p) => has(p.fields, q));
  if (!people.length) return <Empty>Not filled in yet.</Empty>;
  if (!list.length) return <Empty>No matches for this search.</Empty>;
  const cols = [["name", "Name"], ["designation", "Designation"], ["party", "Party"], ["caste", "Caste"],
    ["age", "Age"], ["status", "Status"], ["influence", "Influence"], ["votes", "Votes"], ["phone", "Phone"], ["activity", "Recent Activity"]];
  return (
    <TableShell>
      <thead><tr>{cols.map(([key, l]) => <th key={key} style={TH}>{l}</th>)}</tr></thead>
      <tbody>
        {list.map((p, i) => (
          <tr key={p.id} style={{ background: i % 2 ? "#f8fafd" : "#fff" }}>
            {cols.map(([key]) => {
              const v = p.fields[key];
              return (
                <td key={key} style={{ ...TD, minWidth: key === "activity" ? 220 : undefined }}>
                  {key === "phone" ? <Phone ph={v} revealed={revealed} onReveal={onReveal} />
                    : key === "party" ? <PartyChip party={v} />
                      : key === "status" ? (v ? <Pill color={STATUS_COLOR(v)}>{v}</Pill> : "—")
                        : key === "name" ? <strong style={{ color: INK }}>{titleCase(v)}</strong>
                          : key === "activity" ? <Clamp text={v} lines={2} />
                            : (v ? titleCase(v) : "—")}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
};

/* ------------------------------ derivations ------------------------------- */

const colBy = (block, rx) => (block.columns || []).find((c) => rx.test(c.label));

function derive(d) {
  const allPeople = [];
  d.sections.filter((s) => ["leaders", "organisation"].includes(s.section))
    .forEach((s) => s.blocks.forEach((b) => (b.people || []).forEach((p) => allPeople.push(p))));
  const st = { Active: 0, "Partially Active": 0, Inactive: 0, "Not recorded": 0 };
  allPeople.forEach((p) => {
    const t = String(p.fields.status || "").toLowerCase();
    if (/partial/.test(t)) st["Partially Active"]++;
    else if (/inactive/.test(t)) st.Inactive++;
    else if (/active/.test(t)) st.Active++;
    else st["Not recorded"]++;
  });

  const booths = d.sections.find((s) => s.kind === "booths");
  const bb = booths ? booths.blocks.find((b) => b.rows) : null;
  const clusterC = bb ? colBy(bb, /^cluster$/i) : null;
  const byCluster = {};
  if (bb && clusterC) bb.rows.forEach((r) => { const key = r[clusterC.key] || "—"; byCluster[key] = (byCluster[key] || 0) + 1; });

  const latest = [...(d.elections.Assembly || [])].sort((a, b) => Number(b.year) - Number(a.year))[0] || null;
  const topCaste = [...(d.castes || [])].sort((a, b) => (b.count || 0) - (a.count || 0))[0] || null;
  const comp = d.completeness || [];
  return { st, allPeople, byCluster, latest, topCaste, comp, bb };
}

/* ------------------------------ overview rows ----------------------------- */

function Overview({ d, x }) {
  const k = d.kpis || {};
  const pf = (rx) => { const key = Object.keys(d.profile || {}).find((y) => rx.test(y)); return key ? String(d.profile[key]) : ""; };

  const facts = [
    [faLocationDot, "Zone · Parliament", [pf(/^zone/i), pf(/parliament/i) && `${pf(/parliament/i)} PC`].filter(Boolean).join(" · ")],
    [faUserTie, "MLA (2024)", pf(/mla/i).replace(/\n/g, " ").replace(/\s*\|\s*/g, " · ")],
    [faFlag, "Seat reservation", pf(/reservation/i)],
    [faCity, "Mandals / Wards", [pf(/mandals/i) && `${pf(/mandals/i)} mandal/town`, pf(/wards|gps/i)].filter(Boolean).join(" · ")],
    [faUsers, "Total voters", fmt(k.totalVoters)],
    [faCheckToSlot, "Mandal status", k.mandalStatus],
  ].filter(([, , v]) => v);

  const insights = [];
  const nClusters = Object.keys(x.byCluster).length;
  if (k.weakBooths) insights.push([faCircleExclamation, RED, `${k.weakBooths} weak / focus booths identified${nClusters ? ` across ${nClusters} clusters` : ""}`]);
  if (k.disgruntled) insights.push([faUserSlash, BLUE, `${k.disgruntled} inactive / disgruntled leaders need re-engagement`]);
  if (k.openConflicts) insights.push([faTriangleExclamation, AMBER, `${k.openConflicts} intra-party conflicts not yet resolved`]);
  if (x.latest && x.latest.winner) insights.push([faCheckToSlot, GREEN, `${x.latest.winner} won ${x.latest.year}${x.latest.margin ? ` by ${fmt(x.latest.margin)} votes` : ""}`]);
  if (x.topCaste) {
    const lead = Object.entries(x.topCaste.current || {}).sort((a, b) => b[1] - a[1])[0];
    insights.push([faUsers, PURPLE, `${x.topCaste.caste} is the largest community${x.topCaste.pct ? ` (${x.topCaste.pct}%)` : ""}${lead ? ` — now leaning ${lead[0]} ${lead[1]}%` : ""}`]);
  }
  if (k.probableJoinings) insights.push([faHandshake, TEAL, `${k.probableJoinings} probable joinings from the opposition`]);
  const emptyTabs = x.comp.filter((c) => c.status === "empty").length;
  if (emptyTabs) insights.push([faClipboardCheck, MUTED, `${emptyTabs} proforma sheets not filled in yet`]);

  const stSeg = [
    { label: "Active", value: x.st.Active, color: GREEN },
    { label: "Partially Active", value: x.st["Partially Active"], color: AMBER },
    { label: "Inactive", value: x.st.Inactive, color: RED },
    { label: "Not recorded", value: x.st["Not recorded"], color: "#b0bccd" },
  ].filter((s) => s.value > 0);

  const voteSeg = x.latest ? x.latest.candidates.filter((c) => c.votes).map((c) => ({
    label: c.party, value: c.votes, color: partyColor(c.party),
  })) : [];
  const votesTotal = voteSeg.reduce((s, c) => s + c.value, 0);

  const palette = ["#0d2a5e", "#1e6fd9", "#26a69a", "#4caf50", "#f6a623", "#9c4dcc", "#e53935", "#00897b", "#5c6bc0", "#8d6e63"];
  const clusterBars = Object.entries(x.byCluster).sort((a, b) => b[1] - a[1]).slice(0, 10)
    .map(([lab, v], i) => ({ label: lab.replace(/^cluster[_\s-]*0*(\d+)$/i, "C$1"), full: lab, value: v, color: palette[i % palette.length] }));

  const filled = x.comp.filter((c) => c.status === "filled").length;
  const pct = x.comp.length ? Math.round((filled / x.comp.length) * 100) : 0;

  return (
    <div id="sec-overview" style={{ display: "grid", gap: 16, scrollMarginTop: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 16 }}>
        <Panel icon={faChartColumn} title="Constituency Overview" right={pf(/^zone/i)}>
          {!facts.length ? <Empty>Profile not filled in.</Empty> : (
            <div style={{ display: "grid", gap: 12 }}>
              {facts.map(([ic, l, v]) => (
                <div key={l} style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: "#e8efff", color: BLUE,
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FontAwesomeIcon icon={ic} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={label}>{l}</div>
                    <div style={{ fontSize: 14.5, fontWeight: 800, color: INK }}>{v}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel icon={faLightbulb} title="Key Insights" right="From the proforma">
          {!insights.length ? <Empty>Not enough data for insights yet.</Empty> : (
            <div style={{ display: "grid" }}>
              {insights.slice(0, 6).map(([ic, c, t], i) => (
                <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", padding: "9px 0",
                                      borderBottom: i < Math.min(insights.length, 6) - 1 ? `1px solid ${LINE}` : "none" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: c, color: "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <FontAwesomeIcon icon={ic} />
                  </div>
                  <div style={{ fontSize: 13.5, color: INK, fontWeight: 600, lineHeight: 1.4 }}>{t}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel icon={faUserCheck} title="Leadership Status" right={`${x.allPeople.length} leaders`}>
          {!stSeg.length ? <Empty>No leaders recorded.</Empty> : (
            <>
              <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <Donut segments={stSeg} centerValue={x.allPeople.length} centerLabel="Leaders" />
                <Legend segments={stSeg} />
              </div>
              <div style={{ marginTop: 12, background: "#eef4ff", border: "1px solid #d6e3ff", borderRadius: 8,
                            padding: "8px 12px", fontSize: 12.5, color: "#1e3a8a" }}>
                {Math.round(((x.st.Active || 0) / Math.max(x.allPeople.length, 1)) * 100)}% of recorded leaders are fully active
              </div>
            </>
          )}
        </Panel>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))", gap: 16 }}>
        <Panel icon={faPersonBooth} title="Weak / Focus Booths by Cluster" right={k.weakBooths ? `${k.weakBooths} booths` : ""}>
          {!clusterBars.length ? <Empty>No weak / focus booths recorded.</Empty> : <Bars data={clusterBars} />}
        </Panel>

        <Panel icon={faChartPie} title={`${x.latest ? `${x.latest.year} ` : ""}Assembly Result`}
          right={x.latest && x.latest.margin ? `Margin ${fmt(x.latest.margin)}` : ""}>
          {!voteSeg.length ? <Empty>No election result recorded.</Empty> : (
            <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <Donut segments={voteSeg} centerValue={x.latest.winner} centerLabel="Winner" />
              <div style={{ display: "grid", gap: 6, flex: "1 1 0", minWidth: 0 }}>
                {x.latest.candidates.filter((c) => c.votes).map((c) => (
                  <div key={c.party + c.name} style={{ border: `1px solid ${LINE}`, borderRadius: 8, padding: "5px 9px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, color: INK }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: partyColor(c.party) }} />{c.party}
                      </span>
                      <strong>{Math.round((c.votes / votesTotal) * 1000) / 10}%</strong>
                    </div>
                    <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.35 }}>{c.name} · {fmt(c.votes)} votes</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>

        <Panel icon={faClipboardCheck} title="Proforma Completeness" right={`${filled}/${x.comp.length} sheets`}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Donut size={130} thickness={22}
              segments={[{ label: "Filled", value: filled, color: pct >= 80 ? GREEN : pct >= 50 ? AMBER : RED },
                         { label: "Pending", value: x.comp.length - filled, color: "#e4e9f2" }]}
              centerValue={`${pct}%`} centerLabel="Complete" labelOf={(sg) => String(sg.value)} />
            <div style={{ display: "grid", gap: 6, flex: "1 1 0", minWidth: 0 }}>
              {x.comp.filter((c) => c.status !== "filled").slice(0, 6).map((c) => (
                <div key={c.tab} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5 }}>
                  <span style={{ color: INK }}>{c.tab}</span>
                  <span style={{ flexShrink: 0 }}><Pill color={c.status === "partial" ? AMBER : RED}>{c.status === "partial" ? "Partial" : "Empty"}</Pill></span>
                </div>
              ))}
              {x.comp.every((c) => c.status === "filled") && <div style={{ fontSize: 13, color: GREEN, fontWeight: 700 }}>Every sheet is filled in.</div>}
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}

/* ------------------------------ detail panels ----------------------------- */

function ProfileSection({ d, q }) {
  const mandals = d.sections.find((s) => s.kind === "mandals");
  const mb = mandals ? mandals.blocks.find((b) => b.rows && b.rows.length) : null;
  const history = d.narratives.find((n) => /history/i.test(n.title));
  const issues = d.narratives.find((n) => /issue/i.test(n.title) && n.items);
  const otherNotes = d.narratives.filter((n) => n !== history && n !== issues && n.body);

  return (
    <Panel id="profile" icon={faCity} title="Constituency Profile">
      <SubHead>Mandal / Town Analysis</SubHead>
      {!mb ? <Empty>Mandal / town analysis not filled in yet.</Empty> : mb.rows.filter((r) => has(r, q)).map((r, i) => {
        const nameC = mb.columns[0];
        const statusC = colBy(mb, /status/i);
        const votersC = colBy(mb, /total voters/i);
        const marginC = colBy(mb, /margin/i);
        const rest = mb.columns.filter((c) => ![nameC, statusC, votersC, marginC].includes(c));
        return (
          <div key={i} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16, marginBottom: 12, background: "#fbfcfe" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: INK }}>{r[nameC.key]}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {votersC && r[votersC.key] && <Pill color={NAVY}>{fmt(r[votersC.key])} voters</Pill>}
                {marginC && r[marginC.key] && <Pill color={BLUE}>2024 margin {fmt(r[marginC.key])}</Pill>}
                {statusC && r[statusC.key] && <Pill color={STATUS_COLOR(r[statusC.key])} solid>{r[statusC.key]}</Pill>}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 14, marginTop: 14 }}>
              {rest.filter((c) => r[c.key]).map((c) => (
                <div key={c.key}>
                  <div style={label}>{c.label}</div>
                  <Clamp text={r[c.key]} lines={4} />
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {d.demographics && d.demographics.rows.length > 0 && (
        <>
          <SubHead>Demographics</SubHead>
          <DataTable columns={d.demographics.columns} rows={d.demographics.rows} q={q} revealed={{}} onReveal={() => {}} />
        </>
      )}

      {(history || issues) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 12, marginTop: 16 }}>
          {history && (
            <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 }}>
              <div style={{ ...label, marginBottom: 6 }}>Political history</div>
              <Clamp text={history.body} lines={7} />
            </div>
          )}
          {issues && (
            <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 }}>
              <div style={{ ...label, marginBottom: 6 }}>Key constituency issues</div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#334155", lineHeight: 1.65 }}>
                {issues.items.map((t, i) => <li key={i} style={{ marginBottom: 6 }}>{t}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}
      {otherNotes.map((n, i) => (
        <div key={i} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16, marginTop: 12 }}>
          <div style={{ ...label, marginBottom: 6 }}>{n.title || "Note"}</div>
          <Clamp text={n.body} lines={5} />
        </div>
      ))}
    </Panel>
  );
}

function ElectionsSection({ d }) {
  const [type, setType] = useState("Assembly");
  const list = [...(d.elections[type] || [])].sort((a, b) => Number(b.year) - Number(a.year) || (a.byElection ? 1 : -1));
  const any = d.elections.Assembly.length || d.elections.Parliament.length;
  const toggle = (
    <span className="no-print" style={{ display: "inline-flex", gap: 6 }}>
      {["Assembly", "Parliament"].map((t) => (
        <button key={t} type="button" onClick={() => setType(t)}
          style={{ border: "1px solid rgba(255,255,255,.5)", background: type === t ? "#fff" : "transparent",
                   color: type === t ? NAVY : "#fff", borderRadius: 99, padding: "3px 11px", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
          {t} ({d.elections[t].length})
        </button>
      ))}
    </span>
  );
  return (
    <Panel id="elections" icon={faCheckToSlot} title="Electoral History" right={any ? toggle : null}>
      {!any ? <Empty>No election results recorded in the sheet.</Empty> : (
        <>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14,
                        background: "#f4f7fc", borderRadius: 10, padding: "10px 12px" }}>
            <span style={{ ...label, marginRight: 6 }}>Winners</span>
            {[...list].reverse().map((e) => (
              <span key={e.label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: MUTED }}>{e.year}{e.byElection ? "*" : ""}</span>
                <PartyChip party={e.winner} />
                <span style={{ color: "#cbd5e1" }}>›</span>
              </span>
            ))}
            {list.some((e) => e.byElection) && <span style={{ fontSize: 11, color: MUTED }}>* by-election</span>}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 12 }}>
            {list.map((e) => {
              const max = Math.max(...e.candidates.map((c) => c.votes || 0), 1);
              return (
                <div key={e.label} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 16, breakInside: "avoid",
                                            borderTop: `4px solid ${partyColor(e.winner)}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <div style={{ fontSize: 19, fontWeight: 800, color: INK }}>
                      {e.year} {e.byElection && <span style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>by-election</span>}
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: MUTED }}>Won by</span>
                      <PartyChip party={e.winner} />
                    </div>
                  </div>
                  {e.margin != null && (
                    <div style={{ fontSize: 12.5, color: MUTED, marginTop: 2 }}>Margin <strong style={{ color: INK }}>{fmt(e.margin)}</strong> votes</div>
                  )}
                  <div style={{ display: "grid", gap: 9, marginTop: 12 }}>
                    {e.candidates.map((c, i) => {
                      const share = c.pct != null ? c.pct : c.shareOfListed;
                      const won = partyKey(c.party) && partyKey(c.party) === partyKey(e.winner);
                      return (
                        <div key={i}>
                          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5 }}>
                            <span style={{ color: INK, fontWeight: won ? 800 : 600 }}>
                              <span style={{ color: partyColor(c.party), fontWeight: 800 }}>{c.party}</span>{" "}
                              {c.name}{c.ageGender ? <span style={{ color: MUTED, fontWeight: 500 }}> · {c.ageGender}</span> : null}
                            </span>
                            <span style={{ color: "#334155", whiteSpace: "nowrap" }}>
                              {c.votes != null ? fmt(c.votes) : "—"}{share != null ? <span style={{ color: MUTED }}> ({share}%{c.pct == null ? "†" : ""})</span> : null}
                            </span>
                          </div>
                          <div style={{ height: 9, background: "#eef2f7", borderRadius: 99, marginTop: 4, overflow: "hidden" }}>
                            <div style={{ width: `${((c.votes || 0) / max) * 100}%`, height: "100%", background: partyColor(c.party), opacity: won ? 1 : 0.55 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {e.reason && (
                    <div style={{ marginTop: 12, borderTop: `1px solid ${LINE}`, paddingTop: 10 }}>
                      <div style={label}>Why this result</div>
                      <Clamp text={e.reason} lines={3} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>
            † share of the votes listed in the proforma (the sheet did not record a percentage).
          </div>
        </>
      )}
    </Panel>
  );
}

const SplitBar = ({ split }) => {
  const parts = Object.entries(split || {});
  if (!parts.length) return <span style={{ color: MUTED, fontSize: 12 }}>—</span>;
  return (
    <div style={{ display: "flex", height: 18, borderRadius: 6, overflow: "hidden", minWidth: 160, margin: "0 auto" }}>
      {parts.map(([p, n]) => (
        <div key={p} title={`${p} ${n}%`} style={{
          width: `${n}%`, background: partyColor(p), color: "#fff", fontSize: 10.5, fontWeight: 800,
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
        }}>{n >= 18 ? `${p} ${n}` : ""}</div>
      ))}
    </div>
  );
};

function CasteSection({ d, q }) {
  const castes = (d.castes || []).filter((c) => has(c, q));
  const max = Math.max(...castes.map((c) => c.count || 0), 1);
  const detail = d.sections.find((s) => s.kind === "caste");
  const db = detail ? detail.blocks.find((b) => b.rows && b.rows.length) : null;
  return (
    <Panel id="caste" icon={faUsers} title="Caste & Community" right="Population and political inclination">
      {!castes.length ? <Empty>No caste composition recorded.</Empty> : (
        <TableShell>
          <thead>
            <tr>{["Community", "Voters", "Traditional Inclination", "Current Inclination", "TDP Shift", "Key Leaders"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {castes.map((c, i) => {
              const shift = c.current.TDP != null && c.traditional.TDP != null ? c.current.TDP - c.traditional.TDP : null;
              return (
                <tr key={c.caste} style={{ background: i % 2 ? "#f8fafd" : "#fff" }}>
                  <td style={{ ...TD, fontWeight: 800, color: INK, whiteSpace: "nowrap" }}>{c.caste}</td>
                  <td style={{ ...TD, minWidth: 180 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ flex: 1, height: 9, background: "#eef2f7", borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ width: `${((c.count || 0) / max) * 100}%`, height: "100%", background: NAVY }} />
                      </div>
                      <span style={{ whiteSpace: "nowrap" }}>{fmt(c.count)}{c.pct != null ? ` · ${c.pct}%` : ""}</span>
                    </div>
                  </td>
                  <td style={TD}><SplitBar split={c.traditional} /></td>
                  <td style={TD}><SplitBar split={c.current} /></td>
                  <td style={{ ...TD, textAlign: "center", whiteSpace: "nowrap", fontWeight: 800, color: shift > 0 ? GREEN : shift < 0 ? RED : MUTED }}>
                    {shift == null ? "—" : `${shift > 0 ? "▲ +" : shift < 0 ? "▼ " : ""}${shift} pts`}
                  </td>
                  <td style={{ ...TD, minWidth: 200 }}>{c.leaders || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </TableShell>
      )}

      {db && (
        <>
          <SubHead count={db.rows.length}>Community Concerns & Actionables</SubHead>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
            {db.rows.filter((r) => has(r, q)).map((r, i) => {
              const nameC = colBy(db, /caste name/i);
              const numC = colBy(db, /^number/i);
              const pctC = colBy(db, /^percent/i);
              const fields = [["Key community leaders", colBy(db, /leader/i)], ["Key concerns", colBy(db, /concern/i)],
                ["Suggested actionables", colBy(db, /actionable/i)]];
              return (
                <div key={i} style={{ border: `1px solid ${LINE}`, borderRadius: 12, padding: 14, breakInside: "avoid", borderLeft: `4px solid ${PURPLE}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                    <div style={{ fontSize: 15.5, fontWeight: 800, color: INK }}>{nameC ? r[nameC.key] : "—"}</div>
                    <span style={{ fontSize: 12, color: MUTED }}>
                      {numC && r[numC.key] ? fmt(r[numC.key]) : ""}{pctC && r[pctC.key] ? ` · ${r[pctC.key]}%` : ""}
                    </span>
                  </div>
                  {fields.filter(([, c]) => c && r[c.key]).map(([l, c]) => (
                    <div key={l} style={{ marginTop: 8 }}>
                      <div style={label}>{l}</div>
                      <Clamp text={r[c.key]} lines={3} />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
          {db.issues && db.issues.length > 0 && (
            <div style={{ fontSize: 11.5, color: AMBER, marginTop: 8 }}>Sheet note: {[...new Set(db.issues)].join(" ")}</div>
          )}
        </>
      )}
    </Panel>
  );
}

function LeadersSection({ d, q, revealed, onReveal }) {
  const groups = useMemo(() => {
    const g = [];
    if (d.keyLeaders && d.keyLeaders.people.length) g.push({ label: "Key Leaders (all parties)", people: d.keyLeaders.people });
    d.sections.filter((s) => s.section === "leaders").forEach((s) => s.blocks.forEach((b) => {
      if (!b.people) return;
      g.push({ label: b.title || s.tab, people: b.people, notes: b.notes });
    }));
    return g;
  }, [d]);
  const [tab, setTab] = useState(0);
  const cur = groups[Math.min(tab, groups.length - 1)];
  const hit = (g) => g.people.filter((p) => has(p.fields, q) || has(p.extra, q));
  return (
    <Panel id="leaders" icon={faUserTie} title="Leadership" right="Party leaders, office-bearers and influencers">
      {!groups.length ? <Empty>No leaders recorded.</Empty> : (
        <>
          <div className="no-print" style={{ display: q ? "none" : "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
            {groups.map((g, i) => (
              <button key={g.label + i} type="button" onClick={() => setTab(i)}
                style={{ border: `1px solid ${tab === i ? BLUE : LINE}`, background: tab === i ? BLUE : "#fff",
                         color: tab === i ? "#fff" : INK, borderRadius: 99, padding: "6px 13px", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                {g.label} <span style={{ opacity: 0.75 }}>({g.people.length})</span>
              </button>
            ))}
          </div>
          {q ? (
            groups.filter((g) => hit(g).length).length === 0 ? <Empty>No leaders match this search.</Empty>
              : groups.filter((g) => hit(g).length).map((g, i) => (
                <div key={g.label + i}>
                  <SubHead count={hit(g).length}>{g.label}</SubHead>
                  <PeopleGrid people={g.people} q={q} revealed={revealed} onReveal={onReveal} />
                </div>
              ))
          ) : (
            <PeopleGrid people={cur.people} q={q} revealed={revealed} onReveal={onReveal} />
          )}
          {!q && (cur.notes || []).map((n, i) => (
            <div key={i} style={{ border: `1px solid ${LINE}`, borderLeft: `4px solid ${NAVY}`, borderRadius: 12, padding: 16, marginTop: 12 }}>
              <div style={{ ...label, marginBottom: 6 }}>{n.title || "Note"}</div>
              <Clamp text={n.body} lines={5} />
            </div>
          ))}
        </>
      )}
    </Panel>
  );
}

function OrganisationSection({ d, q, revealed, onReveal, x }) {
  const org = d.sections.filter((s) => s.kind === "org");
  return (
    <Panel id="organisation" icon={faSitemap} title="Organisation & Booths" right="Cluster, unit and booth structure">
      {!org.length && !x.bb && <Empty>No organisation data recorded.</Empty>}
      {org.map((s) => s.blocks.map((b, i) => (
        <div key={s.tab + i}>
          <SubHead count={b.people ? b.people.length : (b.rows || []).length}>{b.title || s.tab}</SubHead>
          {b.people ? <PeopleTable people={b.people} q={q} revealed={revealed} onReveal={onReveal} />
            : <DataTable columns={b.columns} rows={b.rows} q={q} revealed={revealed} onReveal={onReveal} />}
          {b.templateRows > 0 && (
            <div style={{ fontSize: 12, color: AMBER, marginTop: 6 }}>
              {b.templateRows} row{b.templateRows === 1 ? "" : "s"} in this section {b.templateRows === 1 ? "is" : "are"} set up in the sheet but not filled in yet.
            </div>
          )}
        </div>
      )))}
      {x.bb && (
        <>
          <SubHead count={x.bb.rows.length}>Weak / Focus Booth List</SubHead>
          <DataTable columns={x.bb.columns} rows={x.bb.rows} q={q} revealed={revealed} onReveal={onReveal} />
        </>
      )}
    </Panel>
  );
}

function ConflictCard({ b, r }) {
  const cols = b.columns;
  const at = (rx) => cols.findIndex((c) => rx.test(c.label));
  const v = (i) => (i >= 0 && cols[i] ? r[cols[i].key] : "");
  const one = at(/\(one\)/i);
  const two = at(/\(two\)/i);
  const side = (i) => (i < 0 ? null : (
    <div style={{ flex: 1, minWidth: 140 }}>
      <div style={{ fontWeight: 800, color: INK }}>{titleCase(v(i))}</div>
      <div style={{ fontSize: 12, color: MUTED }}>{[v(i + 2) && titleCase(v(i + 2)), v(i + 1) && `${v(i + 1)} yrs`].filter(Boolean).join(" · ")}</div>
    </div>
  ));
  const type = v(at(/type of issue/i));
  const status = v(at(/status/i));
  return (
    <div style={{ border: `1px solid ${LINE}`, borderLeft: `4px solid ${STATUS_COLOR(status)}`, borderRadius: 12, padding: 14, breakInside: "avoid" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{ ...label, color: INK }}>{type || "Conflict"}</div>
        {status && <Pill color={STATUS_COLOR(status)} solid>{status}</Pill>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0" }}>
        {side(one)}
        <span style={{ fontWeight: 800, color: RED, fontSize: 12 }}>vs</span>
        {side(two)}
      </div>
      <Clamp text={v(at(/conflict details|details/i))} lines={3} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
        {v(at(/impact/i)) && <Pill color={NAVY}>Impact: {v(at(/impact/i))}</Pill>}
        {v(at(/required level/i)) && <Pill color={BLUE}>Intervention: {v(at(/required level/i))}</Pill>}
      </div>
    </div>
  );
}

function RiskSection({ d, q, revealed, onReveal }) {
  const risk = d.sections.filter((s) => s.section === "risk");
  return (
    <Panel id="risk" icon={faTriangleExclamation} title="Risks & Opportunities" right="Conflicts, disgruntled leaders, joinings and defections">
      {!risk.length && <Empty>No risk data recorded.</Empty>}
      {risk.map((s) => s.blocks.map((b, i) => (
        <div key={s.tab + i}>
          <SubHead count={b.people ? b.people.length : (b.rows || []).length}>{b.title && !/intervention/i.test(b.title) ? b.title : s.tab}</SubHead>
          {s.kind === "conflicts" ? (
            !b.rows.length ? <Empty>No conflicts recorded.</Empty> : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
                {b.rows.filter((r) => has(r, q)).map((r, k) => <ConflictCard key={k} b={b} r={r} />)}
              </div>
            )
          ) : b.people ? (
            <PeopleGrid people={b.people} q={q} revealed={revealed} onReveal={onReveal}
              accent={/troublemaker/i.test(s.tab) ? PARTY.YSRCP : /disgruntled|inactive|defection/i.test(s.tab) ? RED : /joining/i.test(s.tab) ? GREEN : undefined} />
          ) : (
            <DataTable columns={b.columns} rows={b.rows} q={q} revealed={revealed} onReveal={onReveal} />
          )}
        </div>
      )))}
    </Panel>
  );
}

function GovernanceSection({ d, q, revealed, onReveal }) {
  const gov = d.sections.filter((s) => s.section === "governance");
  return (
    <Panel id="governance" icon={faBuildingColumns} title="Governance & Issues" right="Public issues, projects, hotspots and MLA feedback">
      {!gov.length && <Empty>No governance data recorded.</Empty>}
      {gov.map((s) => s.blocks.map((b, i) => {
        const isIssues = /govt|government/i.test(s.tab) && b.rows && b.rows.length;
        return (
          <div key={s.tab + i}>
            <SubHead count={(b.rows || []).length}>{b.title || s.tab}</SubHead>
            {isIssues ? (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
                {b.rows.filter((r) => has(r, q)).map((r, k) => {
                  const typeC = colBy(b, /type of/i);
                  const detC = colBy(b, /details/i);
                  const locC = colBy(b, /location/i);
                  const areaC = colBy(b, /mandal|town/i);
                  const lvlC = colBy(b, /required level/i);
                  const impC = colBy(b, /impact/i);
                  return (
                    <div key={k} style={{ border: `1px solid ${LINE}`, borderLeft: `4px solid ${TEAL}`, borderRadius: 12, padding: 14, breakInside: "avoid" }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>{typeC ? r[typeC.key] : "Issue"}</div>
                      <div style={{ fontSize: 12, color: MUTED, margin: "2px 0 8px" }}>
                        {[areaC && r[areaC.key], locC && r[locC.key]].filter(Boolean).map(titleCase).join(" · ")}
                      </div>
                      {detC && <Clamp text={r[detC.key]} lines={4} />}
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                        {lvlC && r[lvlC.key] && <Pill color={BLUE}>Needs: {titleCase(r[lvlC.key])}</Pill>}
                        {impC && r[impC.key] && <Pill color={NAVY}>Impact: {r[impC.key]}</Pill>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <DataTable columns={b.columns} rows={b.rows} q={q} revealed={revealed} onReveal={onReveal} />
            )}
          </div>
        );
      }))}
    </Panel>
  );
}

function GenericSection({ id, icon, title, right, list, q, revealed, onReveal }) {
  return (
    <Panel id={id} icon={icon} title={title} right={right}>
      {list.map((s) => s.blocks.map((b, i) => (
        <div key={s.tab + i}>
          <SubHead count={b.people ? b.people.length : (b.rows || []).length}>{b.title || s.tab}</SubHead>
          {b.people ? <PeopleGrid people={b.people} q={q} revealed={revealed} onReveal={onReveal} />
            : <DataTable columns={b.columns} rows={b.rows} q={q} revealed={revealed} onReveal={onReveal} />}
        </div>
      )))}
    </Panel>
  );
}

function QualitySection({ d }) {
  const c = d.completeness || [];
  return (
    <Panel id="quality" icon={faClipboardCheck} title="Data Completeness" right="Status of every proforma sheet">
      <TableShell>
        <thead><tr>{["Sheet", "Section", "Entries", "Unfilled Rows", "Status", "Notes"].map((h) => <th key={h} style={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {c.map((y, i) => (
            <tr key={y.tab} style={{ background: i % 2 ? "#f8fafd" : "#fff" }}>
              <td style={{ ...TD, fontWeight: 700, color: INK }}>{y.tab}</td>
              <td style={{ ...TD, textAlign: "center", textTransform: "capitalize" }}>{y.section}</td>
              <td style={{ ...TD, textAlign: "center" }}>{y.rows}</td>
              <td style={{ ...TD, textAlign: "center" }}>{y.templateRows || "—"}</td>
              <td style={{ ...TD, textAlign: "center" }}>
                <Pill color={y.status === "filled" ? GREEN : y.status === "partial" ? AMBER : RED} solid>
                  {y.status === "filled" ? "Filled" : y.status === "partial" ? "Partial" : "Empty"}
                </Pill>
              </td>
              <td style={{ ...TD, fontSize: 12, color: AMBER }}>{y.issues && y.issues.length ? [...new Set(y.issues)].join(" ") : ""}</td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </Panel>
  );
}

/* ---------------------------------- page ---------------------------------- */

const PAGE_CSS = `
.ap-shell { display: grid; grid-template-columns: 236px 1fr; min-height: 100vh; }
.ap-side { position: sticky; top: 0; height: 100vh; overflow-y: auto; }
.ap-navbtn:hover { background: rgba(255,255,255,.08) !important; }
@media (max-width: 900px) {
  .ap-shell { grid-template-columns: 1fr; }
  .ap-side { position: static; height: auto; }
}
@media print {
  .no-print, .ap-side { display: none !important; }
  .ap-shell { display: block !important; }
  body { background: #fff !important; }
}`;

const KpiCard = ({ icon, value, label: l, grad, sub }) => (
  <div style={{ background: grad, color: "#fff", borderRadius: 12, padding: "16px 18px", position: "relative",
                overflow: "hidden", minHeight: 118, boxShadow: "0 6px 16px rgba(15,35,80,.18)" }}>
    <FontAwesomeIcon icon={icon} style={{ fontSize: 26, opacity: 0.95 }} />
    <FontAwesomeIcon icon={icon} style={{ position: "absolute", right: -8, bottom: -10, fontSize: 86, opacity: 0.14 }} />
    {sub && <div style={{ position: "absolute", top: 14, right: 16, fontSize: 12, fontWeight: 800, opacity: 0.9 }}>{sub}</div>}
    <div style={{ fontSize: 30, fontWeight: 800, marginTop: 8, lineHeight: 1.1 }}>{value == null || value === "" ? "—" : value}</div>
    <div style={{ fontSize: 13.5, fontWeight: 700, opacity: 0.95, marginTop: 2 }}>{l}</div>
  </div>
);

const APProformas = () => {
  const [acs, setAcs] = useState([]);
  const [ac, setAc] = useState("");
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [revealed, setRevealed] = useState({});
  const [active, setActive] = useState("overview");
  const q = search.trim().toLowerCase();

  const headers = async () => ({ Authorization: `Bearer ${await localforage.getItem("token")}` });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`${API_URL}/proformas/list`, { headers: await headers() });
        setAcs(r.data || []);
        const fromUrl = new URLSearchParams(window.location.search).get("ac");
        setAc((r.data || []).some((y) => y.key === fromUrl) ? fromUrl : ((r.data || [])[0] || {}).key || "");
      } catch (e) {
        setErr(e.response?.status === 403 ? "You do not have access to AC proformas." : (e.response?.data?.error || "Could not load the AC list."));
      }
    })();
  }, []);

  const load = async (key, refresh) => {
    if (!key) return;
    setLoading(true); setErr(""); setRevealed({});
    try {
      const r = await api.get(`${API_URL}/proformas/ac/${key}`, { headers: await headers(), params: refresh ? { refresh: 1 } : {}, timeout: 90000 });
      setD(r.data);
      document.title = `${r.data.name} AC · Proforma`;
    } catch (e) {
      setD(null);
      setErr(e.response?.data?.error || "Could not load this proforma.");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (ac) load(ac, false); }, [ac]); // eslint-disable-line react-hooks/exhaustive-deps

  const reveal = async (ref) => {
    try {
      const r = await api.post(`${API_URL}/proformas/ac/${ac}/reveal`, { ref }, { headers: await headers() });
      setRevealed((m) => ({ ...m, [ref]: r.data.number }));
    } catch (e) {
      alert(e.response?.data?.error || "Could not reveal this number.");
    }
  };

  const go = (id) => {
    setActive(id);
    const el = document.getElementById(`sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const x = useMemo(() => (d ? derive(d) : null), [d]);
  const k = (d && d.kpis) || {};
  const pf = (rx) => { const key = Object.keys((d && d.profile) || {}).find((y) => rx.test(y)); return key ? String(d.profile[key]) : ""; };
  const wards = (pf(/wards|gps/i).match(/\d+/) || [""])[0];
  const others = d ? d.sections.filter((s) => s.section === "other") : [];
  const media = d ? d.sections.filter((s) => s.section === "media") : [];
  const nav = NAV.filter(([id]) => (id !== "other" || others.length) && (id !== "media" || media.length));
  const heroBg = d && d.heroImage
    ? `linear-gradient(90deg, rgba(11,35,80,.97) 0%, rgba(11,35,80,.88) 42%, rgba(11,35,80,.25) 100%), url("${d.heroImage}") center/cover`
    : "radial-gradient(circle at 85% 20%, rgba(66,165,245,.35) 0, transparent 38%), radial-gradient(circle at 70% 90%, rgba(38,166,154,.25) 0, transparent 35%), linear-gradient(120deg, #0b2350 0%, #15407f 60%, #1d5aa8 100%)";

  return (
    <div>
      <style>{PAGE_CSS}</style>
      <div className="no-print"><Dashboard /></div>
      <div className="ap-shell" style={{ background: BG }}>
        {/* ---------- sidebar ---------- */}
        <aside className="ap-side" style={{ background: SIDEBAR, color: "#fff", padding: "18px 0" }}>
          <div style={{ padding: "0 18px 16px", borderBottom: "1px solid rgba(255,255,255,.12)" }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "1.2px", opacity: 0.7 }}>PROJECT</div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>Andhra Pradesh</div>
            <div style={{ fontSize: 12.5, opacity: 0.8 }}>AC Proformas</div>
            {acs.length > 0 && (
              <select value={ac} onChange={(e) => setAc(e.target.value)}
                style={{ marginTop: 12, width: "100%", padding: "8px 10px", borderRadius: 8, border: "none", fontWeight: 700, color: INK }}>
                {acs.map((y) => <option key={y.key} value={y.key}>{y.name}{y.district ? ` (${y.district})` : ""}</option>)}
              </select>
            )}
          </div>
          <nav style={{ padding: "10px 0" }}>
            {nav.map(([id, l, ic]) => (
              <button key={id} type="button" className="ap-navbtn" onClick={() => go(id)}
                style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: "none",
                  background: active === id ? "#1d6fe0" : "transparent", color: "#fff",
                  fontWeight: active === id ? 800 : 600, fontSize: 13.5, padding: "11px 20px", cursor: "pointer",
                }}>
                <FontAwesomeIcon icon={ic} style={{ width: 18, opacity: 0.95 }} />
                {l}
              </button>
            ))}
          </nav>
          <div style={{ padding: "18px 20px", fontSize: 12, opacity: 0.7, display: "flex", gap: 8, alignItems: "center" }}>
            <FontAwesomeIcon icon={faLock} /> Restricted · contact numbers masked, reveals logged
          </div>
        </aside>

        {/* ---------- main ---------- */}
        <main style={{ minWidth: 0 }}>
          <div style={{ background: heroBg, color: "#fff", padding: "26px 28px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", gap: 26, alignItems: "center", flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.05 }}>{d ? `${d.name} AC` : "AC Proformas"}</div>
                  <div style={{ fontSize: 16, opacity: 0.92, marginTop: 4 }}>Constituency Assessment Dashboard</div>
                </div>
                {d && (
                  <div style={{ borderLeft: "2px solid rgba(255,255,255,.35)", paddingLeft: 22, display: "grid", gap: 7, fontSize: 13.5 }}>
                    <span><FontAwesomeIcon icon={faLocationDot} style={{ width: 16 }} /> {[pf(/^zone/i), pf(/parliament/i) && `${pf(/parliament/i)} PC`].filter(Boolean).join(" – ") || "—"}</span>
                    <span><FontAwesomeIcon icon={faUsers} style={{ width: 16 }} /> {[wards && `${wards} Wards`, pf(/mandals/i) && `${pf(/mandals/i)} Mandal/Town`].filter(Boolean).join(" / ") || "—"}</span>
                    <span><FontAwesomeIcon icon={faCalendarDays} style={{ width: 16 }} /> Last refreshed {new Date(d.fetchedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
                  </div>
                )}
              </div>
              <div className="no-print" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ position: "relative" }}>
                  <FontAwesomeIcon icon={faMagnifyingGlass} style={{ position: "absolute", left: 12, top: 11, color: MUTED, fontSize: 13 }} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search across modules…"
                    style={{ padding: "9px 14px 9px 34px", borderRadius: 99, border: "none", minWidth: 250, fontSize: 13 }} />
                </div>
                <button type="button" onClick={() => load(ac, true)} disabled={loading} title="Re-read the sheet"
                  style={{ background: "rgba(255,255,255,.15)", color: "#fff", border: "1px solid rgba(255,255,255,.35)", borderRadius: 99, padding: "8px 14px", fontWeight: 700, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faRotate} /> {loading ? "Loading…" : "Refresh"}
                </button>
                <button type="button" onClick={() => window.print()} disabled={!d}
                  style={{ background: "#fff", color: NAVY, border: "none", borderRadius: 99, padding: "8px 14px", fontWeight: 800, cursor: "pointer" }}>
                  <FontAwesomeIcon icon={faPrint} /> Print / PDF
                </button>
              </div>
            </div>
            {d && (
              <div className="no-print" style={{ fontSize: 11.5, opacity: 0.75, marginTop: 14 }}>
                Source: {d.sheetTitle} · <a href={d.sheetUrl} target="_blank" rel="noreferrer" style={{ color: "#bcd3ff" }}>open sheet</a>
              </div>
            )}
          </div>

          <div style={{ padding: "18px 22px 40px" }}>
            {err && <div style={{ background: "#fff", border: `1px solid ${LINE}`, borderLeft: `4px solid ${RED}`, borderRadius: 10, padding: 16, color: RED, fontWeight: 700, marginBottom: 16 }}>{err}</div>}
            {loading && !d && <div style={{ padding: 40, textAlign: "center", color: MUTED }}>Reading the proforma sheet…</div>}

            {d && x && (
              <div style={{ display: "grid", gap: 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 14 }}>
                  <KpiCard icon={faUsers} grad={GRAD.navy} value={fmt(k.totalVoters)} label="Total Voters" />
                  <KpiCard icon={faLocationDot} grad={GRAD.green} value={wards || pf(/mandals/i)} label={wards ? "Wards" : "Mandals / Towns"} />
                  <KpiCard icon={faPersonBooth} grad={GRAD.purple} value={k.weakBooths} label="Weak Focus Booths" />
                  <KpiCard icon={faUserSlash} grad={GRAD.orange} value={k.disgruntled} label="Disgruntled Leaders" />
                  <KpiCard icon={faCircleExclamation} grad={GRAD.teal} value={k.govtIssues} label="Major Issues" />
                  <KpiCard icon={faTriangleExclamation} grad={GRAD.red} value={k.openConflicts} label="Open Conflicts" />
                  <KpiCard icon={faCheckToSlot} grad={GRAD.blue} value={k.margin2024 ? fmt(k.margin2024) : "—"} label="2024 Margin" sub={k.winner2024} />
                </div>

                <Overview d={d} x={x} />
                <ProfileSection d={d} q={q} />
                <ElectionsSection d={d} />
                <CasteSection d={d} q={q} />
                <LeadersSection d={d} q={q} revealed={revealed} onReveal={reveal} />
                <OrganisationSection d={d} q={q} revealed={revealed} onReveal={reveal} x={x} />
                <RiskSection d={d} q={q} revealed={revealed} onReveal={reveal} />
                <GovernanceSection d={d} q={q} revealed={revealed} onReveal={reveal} />
                {media.length > 0 && <GenericSection id="media" icon={faNewspaper} title="Media" right="Stringers and local channels" list={media} q={q} revealed={revealed} onReveal={reveal} />}
                {others.length > 0 && <GenericSection id="other" icon={faClipboardCheck} title="Other Sheets" list={others} q={q} revealed={revealed} onReveal={reveal} />}
                <QualitySection d={d} />
              </div>
            )}
          </div>

          <div style={{ background: NAVY, color: "rgba(255,255,255,.8)", fontSize: 12, padding: "10px 22px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
            <span>{d ? `${d.name} Assembly Constituency` : "AC Proformas"} | Project Andhra Pradesh</span>
            <span>ShowTime Consulting · STC Workplace</span>
          </div>
        </main>
      </div>
    </div>
  );
};

export default APProformas;
